"use strict";

const pressSelectors = {
  copy: "[data-press-copy]",
  trigger: "[data-press-trigger]",
  panel: "[data-press-panel]",
};

class PressCallout extends HTMLElement {
  constructor() {
    super();

    this.copyEl = null;
    this.triggers = [];
    this.panels = [];

    this.activeIndex = 0;

    // Autoplay scheduling
    this._timerId = null;
    this._paused = false;
    this._stopped = false;
    this._lastTick = 0;

    this.interval = 4000;
    this.prefersReducedMotion = false;
    this.canHover = false;

    // User interaction cooldown
    this.cooldownMs = 30_000;
    this._cooldownUntil = 0;

    this._ro = null;
    this._raf = null;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onTriggerEnter = this.onTriggerEnter.bind(this);
    this.onTriggerLeave = this.onTriggerLeave.bind(this);
    this.onTriggerFocusIn = this.onTriggerFocusIn.bind(this);
    this.onTriggerKeyDown = this.onTriggerKeyDown.bind(this);

    this.onResize = this.onResize.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  connectedCallback() {
    this.copyEl = this.querySelector(pressSelectors.copy);
    this.triggers = Array.from(this.querySelectorAll(pressSelectors.trigger));
    this.panels = Array.from(this.querySelectorAll(pressSelectors.panel));

    if (!this.copyEl || this.triggers.length === 0 || this.panels.length === 0) return;

    this.prefersReducedMotion = !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this.canHover = !!window.matchMedia && window.matchMedia("(hover: hover)").matches;

    this.interval = Math.max(1000, Number(this.dataset.interval) || 4000);
    this.cooldownMs = Math.max(0, Number(this.dataset.cooldown) || 30_000);

    const fromDom = this.triggers.findIndex((b) => b.getAttribute("aria-pressed") === "true");
    this.activeIndex = fromDom >= 0 ? fromDom : 0;

    this.triggers.forEach((btn) => {
      btn.addEventListener("click", this.onTriggerClick);
      btn.addEventListener("focusin", this.onTriggerFocusIn);
      btn.addEventListener("keydown", this.onTriggerKeyDown);

      if (this.canHover) {
        btn.addEventListener("mouseenter", this.onTriggerEnter, { passive: true });
        btn.addEventListener("mouseleave", this.onTriggerLeave, { passive: true });
      }
    });

    // Pause autoplay when tab is hidden to prevent "catch up"
    document.addEventListener("visibilitychange", this.onVisibilityChange);

    this._ro = new ResizeObserver(this.onResize);
    this._ro.observe(this);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!this.isConnected) return;
      });
    }

    this._stopped = false;
    this._paused = false;

    this.setActive(this.activeIndex, { userInitiated: false });

    this.scheduleNextTick({ resetBase: true });
  }

  disconnectedCallback() {
    this._stopped = true;
    this.clearTimer();

    if (this._ro) {
      this._ro.disconnect();
      this._ro = null;
    }

    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }

    document.removeEventListener("visibilitychange", this.onVisibilityChange);

    this.triggers.forEach((btn) => {
      btn.removeEventListener("click", this.onTriggerClick);
      btn.removeEventListener("focusin", this.onTriggerFocusIn);
      btn.removeEventListener("keydown", this.onTriggerKeyDown);

      if (this.canHover) {
        btn.removeEventListener("mouseenter", this.onTriggerEnter);
        btn.removeEventListener("mouseleave", this.onTriggerLeave);
      }
    });

    this.copyEl = null;
    this.triggers = [];
    this.panels = [];
  }

  // -----------------------------
  // Helpers
  // -----------------------------

  now() {
    return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
  }

  clearTimer() {
    if (!this._timerId) return;
    window.clearTimeout(this._timerId);
    this._timerId = null;
  }

  isAutoplayAllowed() {
    if (this._stopped) return false;
    if (this.prefersReducedMotion) return false;
    if (document.visibilityState !== "visible") return false;
    if (this._paused) return false;

    const t = this.now();
    if (t < this._cooldownUntil) return false;

    return true;
  }

  // Schedule one tick at a time (prevents compounding/catch-up)
  scheduleNextTick({ resetBase = false } = {}) {
    if (this._stopped) return;
    this.clearTimer();

    if (resetBase) this._lastTick = this.now();

    const t = this.now();
    const cooldownRemaining = Math.max(0, this._cooldownUntil - t);
    const baseDelay = this.interval;

    const delay = cooldownRemaining > 0 ? cooldownRemaining : baseDelay;

    this._timerId = window.setTimeout(() => this.tick(), delay);
  }

  tick() {
    if (this._stopped) return;

    if (!this.isAutoplayAllowed()) {
      this.scheduleNextTick({ resetBase: true });
      return;
    }

    this.next();

    this._lastTick = this.now();
    this.scheduleNextTick({ resetBase: false });
  }

  markUserInteracted() {
    const t = this.now();
    this._cooldownUntil = Math.max(this._cooldownUntil, t + this.cooldownMs);

    this.scheduleNextTick({ resetBase: true });
  }

  // -----------------------------
  // Events
  // -----------------------------

  onResize() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      if (!this.isConnected) return;
    });
  }

  onVisibilityChange() {
    // When leaving the tab, cancel timers so nothing accumulates.
    if (document.visibilityState !== "visible") {
      this.clearTimer();
      return;
    }

    // When returning, restart
    this.scheduleNextTick({ resetBase: true });
  }

  onTriggerClick(e) {
    const i = Number(e.currentTarget?.dataset?.index);
    if (!Number.isFinite(i)) return;

    this.markUserInteracted();
    this.setActive(i, { userInitiated: true });
  }

  onTriggerEnter(e) {
    // Hover should pause, and also count as interaction to disable autoplay for 30s
    this._paused = true;

    const i = Number(e.currentTarget?.dataset?.index);
    if (!Number.isFinite(i)) return;

    this.markUserInteracted();
    this.setActive(i, { userInitiated: true });
  }

  onTriggerLeave() {
    this._paused = false;
    // don’t “restart” interval; just reschedule a clean next tick
    this.scheduleNextTick({ resetBase: true });
  }

  onTriggerFocusIn() {
    this.markUserInteracted();
  }

  onTriggerKeyDown(e) {
    const key = e.key;
    if (key === "Enter" || key === " ") {
      this.markUserInteracted();
    }
  }

  // -----------------------------
  // UI
  // -----------------------------

  setActive(nextIndex, { userInitiated = false } = {}) {
    const idx = Math.max(0, Math.min(this.triggers.length - 1, Number(nextIndex)));
    this.activeIndex = idx;

    this.triggers.forEach((btn, i) => {
      btn.setAttribute("aria-pressed", i === idx ? "true" : "false");
    });

    this.panels.forEach((panel, i) => {
      const isActive = i === idx;
      panel.classList.toggle("is-active", isActive);
      panel.setAttribute("aria-hidden", isActive ? "false" : "true");
    });

    // If user initiates, ensure we don't immediately auto-advance afterwards
    if (userInitiated) this.markUserInteracted();
  }

  next() {
    this.setActive((this.activeIndex + 1) % this.triggers.length, { userInitiated: false });
  }
}

if (!customElements.get("press-callout")) {
  customElements.define("press-callout", PressCallout);
}
